/**
 * Mo at work and what Mo needs from the owner:
 * - MoWorking: what Mo is doing right now (current step, elapsed time, stop, the steps of
 *   this run), in the conversation under the last message (BuildTranscript `live`).
 * - BuildFocus: right above the input, only what the owner acts on: the cards where Mo's
 *   questions, key requests, app connections and confirmations are answered, and the note
 *   when Mo works in another conversation. While Mo works, the input is blocked (BuildView).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare, faChevronRight, faCircleCheck, faCircleQuestion, faKey, faPlug, faShieldHalved, faStop,
} from '@fortawesome/free-solid-svg-icons';
import { guardWrite } from '../../utils/permissions';
import { activityText, StepList } from './activity';

export { activityText };

function useElapsed(since) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!since) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);
  if (!since) return '';
  const s = Math.max(0, Math.floor(now / 1000 - since));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} min`;
}

/** Mo at work in this conversation: shown under the last message while the run lasts. */
export function MoWorking({ builder, liveSteps = [] }) {
  const { t } = useTranslation();
  const { state, conversationId } = builder;
  const runConv = state?.run_conversation_id;
  const here = !!state?.running && (!runConv || runConv === conversationId);
  const elapsed = useElapsed(here ? state?.started_at : null);
  // once Mo answers, its streaming text is the status; the card is back if Mo goes on working after it
  if (!here || state?.activity_key === 'agent_studio_act_writing') return null;
  const activity = activityText(t, state?.activity_key, state?.activity_params, state?.activity);
  return <LiveWork activity={activity} elapsed={elapsed} onStop={builder.stop} queued={state?.queued} steps={liveSteps} />;
}

export default function BuildFocus({ builder }) {
  const { t } = useTranslation();
  const { state, pending, conversationId } = builder;
  const runConv = state?.run_conversation_id;
  const elsewhere = !!state?.running && !!runConv && runConv !== conversationId;

  if (!elsewhere && pending.length === 0) return null;
  return (
    <div className="as-focus">
      {elsewhere && (
        <div className="as-focus-elsewhere">
          <span className="as-focus-pulse" /> {t('agent_studio_running_elsewhere')}
          <button type="button" className="as-btn-link" onClick={() => builder.openConversation(runConv)}>
            {t('agent_studio_open_conversation')}
          </button>
        </div>
      )}
      {pending.map((p) => {
        if (p.kind === 'questions') {
          return <QuestionsCard key={p.id} item={p} onAnswer={builder.answer} onCancel={builder.cancelQuestions} />;
        }
        if (p.kind === 'secret') return <SecretCard key={p.id} item={p} onProvide={builder.provideSecret} />;
        if (p.kind === 'confirm') return <ConfirmCard key={p.id} item={p} onAnswer={builder.confirmAction} />;
        return <ConnectCard key={p.id} item={p} onDone={builder.connectionDone} />;
      })}
    </div>
  );
}

function LiveWork({ activity, elapsed, onStop, queued, steps }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const stop = async () => {
    if (!guardWrite()) return;
    setError('');
    try {
      await onStop();
    } catch (e) {
      setError(e.message || String(e));
    }
  };
  const stepsText = steps.length === 1 ? t('agent_studio_steps_one') : t('agent_studio_steps', { n: steps.length });
  const meta = [steps.length ? stepsText : null, elapsed || null,
    queued ? t('agent_studio_queued', { n: queued }) : null].filter(Boolean).join(' · ');
  return (
    <div className={`as-working ${open ? 'is-open' : ''}`} role="status" aria-live="polite">
      <div className="as-working-row">
        <span className="as-working-orb" aria-hidden="true"><i /></span>
        <button type="button" className="as-working-main" onClick={() => setOpen((o) => !o)} disabled={!steps.length}
          aria-expanded={open}>
          <span className="as-working-text">{activity}</span>
          {meta && (
            <span className="as-working-meta">
              {meta}
              {steps.length > 0 && <FontAwesomeIcon icon={faChevronRight} className={`as-chevron ${open ? 'is-open' : ''}`} />}
            </span>
          )}
        </button>
        <button type="button" className="as-working-stop" onClick={stop} title={t('agent_studio_stop_title')}>
          <FontAwesomeIcon icon={faStop} /> <span>{t('agent_studio_stop')}</span>
        </button>
      </div>
      {open && steps.length > 0 && <div className="as-working-steps"><StepList items={steps} /></div>}
      {error && <div className="as-error">{t('agent_studio_action_failed', { error })}</div>}
    </div>
  );
}

function QuestionsCard({ item, onAnswer, onCancel }) {
  const { t } = useTranslation();
  const questions = item.data.questions || [];
  const [answers, setAnswers] = useState({});
  const [texts, setTexts] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pick = (q, opt) => setAnswers((a) => {
    if (q.multi_select) {
      const cur = a[q.id] || [];
      return { ...a, [q.id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    }
    return { ...a, [q.id]: a[q.id] === opt ? '' : opt };
  });

  const complete = questions.every((q) => {
    const a = answers[q.id];
    return (Array.isArray(a) ? a.length > 0 : !!a) || (texts[q.id] || '').trim();
  });

  const submit = async () => {
    if (!complete || busy || !guardWrite()) return;
    setBusy(true);
    const out = {};
    for (const q of questions) {
      const a = answers[q.id];
      const txt = (texts[q.id] || '').trim();
      out[q.id] = Array.isArray(a) ? (txt ? [...a, txt] : a) : [a, txt].filter(Boolean).join(' – ');
    }
    setError('');
    try {
      await onAnswer(item.id, out);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };
  // Cancel (Robert, 17.10.2026): the card closes, Mo is not started
  const cancel = async () => {
    if (busy || !onCancel || !guardWrite()) return;
    setBusy(true);
    setError('');
    try {
      await onCancel(item.id);
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  return (
    <div className="as-card">
      <div className="as-card-title"><span className="as-card-ic"><FontAwesomeIcon icon={faCircleQuestion} /></span> {t('agent_studio_question_title')}</div>
      {item.data.intro && <p className="as-card-intro">{item.data.intro}</p>}
      {questions.map((q) => (
        <div key={q.id} className="as-question">
          <div className="as-question-text">{q.question}</div>
          {q.options.length > 0 && (
            <div className="as-options">
              {q.options.map((o) => {
                const a = answers[q.id];
                const active = Array.isArray(a) ? a.includes(o) : a === o;
                return (
                  <button type="button" key={o} className={`as-option ${active ? 'is-active' : ''}`} onClick={() => pick(q, o)}>
                    {o}
                  </button>
                );
              })}
            </div>
          )}
          {(q.allow_text || q.options.length === 0) && (
            <input
              className="as-input"
              value={texts[q.id] || ''}
              onChange={(e) => setTexts((x) => ({ ...x, [q.id]: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder={q.options.length ? t('agent_studio_answer_own') : t('agent_studio_answer_placeholder')}
            />
          )}
        </div>
      ))}
      {error && <div className="as-error">{t('agent_studio_action_failed', { error })}</div>}
      <div className="as-card-actions">
        {onCancel && (
          <button type="button" className="as-btn as-btn-ghost" disabled={busy} onClick={cancel}>{t('cancel')}</button>
        )}
        <button type="button" className="as-btn as-btn-primary" disabled={!complete || busy} onClick={submit}>
          {busy ? t('agent_studio_sending') : t('agent_studio_answer_send')}
        </button>
      </div>
    </div>
  );
}

function SecretCard({ item, onProvide }) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const send = async (v) => {
    if (busy || !guardWrite()) return;
    setBusy(true);
    setError('');
    try {
      await onProvide(item.id, v);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="as-card">
      <div className="as-card-title"><span className="as-card-ic"><FontAwesomeIcon icon={faKey} /></span> {t('agent_studio_secret_title', { label: item.data.label })}</div>
      <p className="as-card-intro as-prewrap">{item.data.explanation}</p>
      {item.data.help_url && (
        <a className="as-link" href={item.data.help_url} target="_blank" rel="noopener noreferrer">
          {t('agent_studio_secret_help')} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
      )}
      <div className="as-row">
        <input
          type="password"
          autoComplete="off"
          className="as-input as-mono"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && value.trim() && send(value)}
          placeholder={t('agent_studio_secret_placeholder')}
        />
        <button type="button" className="as-btn as-btn-primary" disabled={!value.trim() || busy} onClick={() => send(value)}>
          <FontAwesomeIcon icon={faShieldHalved} /> {t('agent_studio_secret_save')}
        </button>
      </div>
      {error && <div className="as-error">{t('agent_studio_action_failed', { error })}</div>}
      <div className="as-card-foot">
        <span>{t('agent_studio_secret_note')}</span>
        <button type="button" className="as-btn-link" disabled={busy} onClick={() => send(null)}>{t('agent_studio_later')}</button>
      </div>
    </div>
  );
}

function detailValue(v) {
  if (v === null || v === undefined) return '–';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** A platform change a specialist prepared: it runs only after the owner's click (exactly these values). */
function ConfirmCard({ item, onAnswer }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const d = item.data;
  const details = Object.entries(d.details || {});
  const answer = async (approved) => {
    if (busy || !guardWrite()) return;
    setBusy(true);
    setError('');
    try {
      await onAnswer(item.id, approved);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="as-card" role="group" aria-label={t('agent_studio_confirm_title', { title: d.title })}>
      <div className="as-card-title"><span className="as-card-ic"><FontAwesomeIcon icon={faCircleCheck} /></span> {t('agent_studio_confirm_title', { title: d.title })}</div>
      {details.length > 0 && (
        <ul className="as-confirm-details">
          {details.map(([k, v]) => <li key={k}><span className="as-confirm-key">{k}</span> {detailValue(v)}</li>)}
        </ul>
      )}
      <div className="as-row">
        <button type="button" className="as-btn as-btn-primary" disabled={busy} onClick={() => answer(true)}>
          {busy ? t('agent_studio_confirm_running') : t('agent_studio_confirm_run')}
        </button>
        <button type="button" className="as-btn as-btn-ghost" disabled={busy} onClick={() => answer(false)}>
          {t('agent_studio_confirm_cancel')}
        </button>
      </div>
      {error && <div className="as-error">{t('agent_studio_action_failed', { error })}</div>}
      <div className="as-card-foot"><span>{t('agent_studio_confirm_note')}</span></div>
    </div>
  );
}

function ConnectCard({ item, onDone }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const provider = item.data.provider || '';
  const name = provider.charAt(0).toUpperCase() + provider.slice(1);
  const done = async (connected) => {
    if (busy || !guardWrite()) return;
    setBusy(true);
    setError('');
    try {
      await onDone(item.id, connected);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="as-card">
      <div className="as-card-title"><span className="as-card-ic"><FontAwesomeIcon icon={faPlug} /></span> {t('agent_studio_connect_title', { app: name })}</div>
      {item.data.reason && <p className="as-card-intro">{item.data.reason}</p>}
      <div className="as-row">
        <a className="as-btn as-btn-primary" href="/agent-studio/apps" target="_blank" rel="noopener noreferrer">
          {t('agent_studio_connect_open', { app: name })} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
        <button type="button" className="as-btn as-btn-ghost" disabled={busy} onClick={() => done(true)}>
          {t('agent_studio_connect_done')}
        </button>
      </div>
      {error && <div className="as-error">{t('agent_studio_action_failed', { error })}</div>}
      <div className="as-card-foot">
        <span>{t('agent_studio_connect_note')}</span>
        <button type="button" className="as-btn-link" disabled={busy} onClick={() => done(false)}>{t('agent_studio_later')}</button>
      </div>
    </div>
  );
}
